import { Heart, Car, Fuel, Gauge, Settings2, Calendar, ArrowRight } from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAppData } from '@/contexts/AppDataContext';
import { useTheme } from '@/contexts/ThemeContext';
import { getLayout5ThemeStyle } from './theme';
import { secureGetItem, secureSetItem } from '@/lib/storage';
import { generateVehicleSlug } from '@/lib/slug';
import { useStore } from '@/contexts/StoreContext';
import { generateResponsiveImageUrls, getImageLoadingStrategy } from '@/lib/imageOptimization';
import { getBrandLogo } from '@/lib/brandLogos';
import './styles.css';

interface Vehicle {
  id: number;
  brand: string;
  model: string;
  version: string;
  price: number;
  photos: string[];
  mileage: number;
  fuel: string;
  manufactureYear: number;
  modelYear: number;
  color: string;
  transmission: string;
  isZeroKm: boolean;
  store?: string;
  type?: number;
  changed?: {
    featured?: boolean;
  };
}

interface VehicleListCardProps {
  vehicle: Vehicle;
  whatsappUrl?: string;
}

const formatLabel = (value: string) =>
  value
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const VehicleListCard = ({ vehicle }: VehicleListCardProps) => {
  const { config } = useAppData();
  const { colors } = useTheme();
  const themeStyle = getLayout5ThemeStyle(colors);
  const { availableStores, selectedStore } = useStore();
  const [isFavorited, setIsFavorited] = useState(false);
  const [brandLogoFailed, setBrandLogoFailed] = useState(false);
  const [vehicleSlug, setVehicleSlug] = useState<string>(() => {
    const brand = (vehicle.brand || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-');
    const model = (vehicle.model || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-');
    return `carro/${brand}-${model}-${vehicle.modelYear || vehicle.id}-${vehicle.id}`;
  });

  const secondaryColor = colors?.secondary || '#64748b';
  const buttonColor = colors?.button || '#3b82f6';
  const titleColor = colors?.title || '#1e293b';
  const textColor = colors?.text || '#64748b';

  const storeId = vehicle.store;
  const vehicleStore = useMemo(() => {
    if (!config?.stores) return null;
    return storeId
      ? config.stores.find((s: any) => s._id === storeId)
      : (selectedStore || config.stores[0]);
  }, [config?.stores, storeId, selectedStore]);
  const showStoreBadge = availableStores.length > 1 && vehicleStore && storeId;
  const isFeatured = !!vehicle.changed?.featured;

  useEffect(() => {
    const init = async () => {
      try {
        secureGetItem('favorites').then(favorites => {
          if (favorites && Array.isArray(favorites)) {
            setIsFavorited(favorites.includes(vehicle.id));
          }
        }).catch(() => {});

        generateVehicleSlug(vehicle).then(slug => {
          setVehicleSlug(slug);
        }).catch(() => {});
      } catch {
        /* card still renders */
      }
    };
    init();
    setBrandLogoFailed(false);
  }, [vehicle.id, vehicle.brand, vehicle.type]);

  const toggleFavorite = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const favorites = await secureGetItem('favorites') || [];
    let updatedFavorites = Array.isArray(favorites) ? [...favorites] : [];

    if (updatedFavorites.includes(vehicle.id)) {
      updatedFavorites = updatedFavorites.filter((id: number) => id !== vehicle.id);
      setIsFavorited(false);
    } else {
      updatedFavorites.push(vehicle.id);
      setIsFavorited(true);
    }

    await secureSetItem('favorites', updatedFavorites);
    window.dispatchEvent(new Event('favoritesUpdated'));
  };

  const formatPrice = (price: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(price);

  const formatMileage = (mileage: number) => {
    if (mileage === 0) return 'Zero KM';
    return new Intl.NumberFormat('pt-BR').format(mileage) + ' km';
  };

  const brandLogo = useMemo(
    () => getBrandLogo(vehicle.brand, vehicle.type),
    [vehicle.brand, vehicle.type]
  );
  const imageUrl = vehicle.photos?.[0];
  const optimized = imageUrl ? generateResponsiveImageUrls(imageUrl, 480, 300) : null;
  const loadingStrategy = getImageLoadingStrategy(true, true);

  const specs = [
    {
      key: 'ano',
      label: 'Ano',
      value: vehicle.modelYear ? String(vehicle.modelYear) : '—',
      icon: Calendar,
    },
    {
      key: 'cambio',
      label: 'Câmbio',
      value: vehicle.transmission || '—',
      icon: Settings2,
    },
    {
      key: 'km',
      label: 'Quilometragem',
      value: vehicle.mileage !== undefined && vehicle.mileage !== null
        ? formatMileage(vehicle.mileage)
        : '—',
      icon: Gauge,
    },
    {
      key: 'combustivel',
      label: 'Combustível',
      value: vehicle.fuel || '—',
      icon: Fuel,
    },
  ];

  return (
    <article className="wl5-vehicle-list-card group" style={themeStyle}>
      <Link to={`/estoque/${vehicleSlug}`} className="wl5-vehicle-list-link">
        <div className="wl5-vehicle-list-media">
          {isFeatured && (
            <span className="wl5-vehicle-list-ribbon" style={{ backgroundColor: buttonColor }}>
              Destaque
            </span>
          )}

          {optimized ? (
            <img
              src={optimized.src}
              srcSet={optimized.srcSet}
              sizes={optimized.sizes || '(max-width: 768px) 100vw, 360px'}
              alt={`${vehicle.brand} ${vehicle.model}`}
              className="wl5-vehicle-list-img"
              loading={loadingStrategy.loading}
              fetchpriority={loadingStrategy.fetchpriority}
              decoding="async"
              width={optimized.width}
              height={optimized.height}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          ) : (
            <div className="wl5-vehicle-list-img wl5-vehicle-list-img--empty">
              <Car className="h-10 w-10 opacity-40" />
            </div>
          )}

          <div className="wl5-vehicle-list-media-tools">
            {vehicle.isZeroKm && (
              <span className="wl5-vehicle-list-media-pill">Zero KM</span>
            )}
            {showStoreBadge && (
              <span
                className="wl5-vehicle-list-media-pill wl5-vehicle-list-media-pill--store"
                style={{ backgroundColor: secondaryColor }}
              >
                {vehicleStore?.company}
              </span>
            )}
          </div>
        </div>

        <div className="wl5-vehicle-list-content">
          <div className="wl5-vehicle-list-price-badge" style={{ backgroundColor: buttonColor }}>
            <span className="wl5-vehicle-list-price-label">Preço</span>
            <span className="wl5-vehicle-list-price-value">{formatPrice(vehicle.price)}</span>
          </div>

          <div className="wl5-vehicle-list-main">
            <header className="wl5-vehicle-list-header">
              <h3 className="wl5-vehicle-list-title" style={{ color: titleColor }}>
                {formatLabel(vehicle.brand)} {formatLabel(vehicle.model)}
              </h3>
              {vehicle.version && (
                <p className="wl5-vehicle-list-version">{formatLabel(vehicle.version)}</p>
              )}
            </header>

            <div className="wl5-vehicle-list-specs">
              {specs.map(({ key, label, value, icon: Icon }) => (
                <div key={key} className="wl5-vehicle-list-spec">
                  <Icon className="wl5-vehicle-list-spec-icon" style={{ color: textColor }} />
                  <span className="wl5-vehicle-list-spec-label">{label}</span>
                  <span className="wl5-vehicle-list-spec-value" style={{ color: titleColor }}>
                    {value}
                  </span>
                </div>
              ))}
            </div>

            <div className="wl5-vehicle-list-actions">
              <span
                className="wl5-vehicle-list-cta"
                style={{ borderColor: buttonColor, color: buttonColor }}
              >
                Ver detalhes
                <ArrowRight className="h-3.5 w-3.5" />
              </span>

              <button
                type="button"
                onClick={toggleFavorite}
                className="wl5-vehicle-list-favorite"
                style={{
                  borderColor: isFavorited ? buttonColor : 'var(--wl5-border)',
                  color: isFavorited ? buttonColor : textColor,
                }}
                title={isFavorited ? 'Remover dos favoritos' : 'Favoritar'}
                aria-label={isFavorited ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
              >
                <Heart className="h-4 w-4" fill={isFavorited ? buttonColor : 'none'} />
              </button>

              <span
                className="wl5-vehicle-list-brand"
                title={formatLabel(vehicle.brand)}
                aria-label={formatLabel(vehicle.brand)}
              >
                {brandLogo && !brandLogoFailed ? (
                  <img
                    src={brandLogo}
                    alt=""
                    className="wl5-vehicle-list-brand-img"
                    onError={() => setBrandLogoFailed(true)}
                  />
                ) : (
                  <span className="wl5-vehicle-list-brand-fallback" style={{ color: buttonColor }}>
                    {(vehicle.brand || '?').charAt(0).toUpperCase()}
                  </span>
                )}
              </span>
            </div>
          </div>
        </div>
      </Link>
    </article>
  );
};
